import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { reviews } from '@/mocks/comics';

const contactFormUrl = 'https://readdy.ai/api/form/d9pcum2qs38sj6jc7m00';

export default function About() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫网',
    url: 'https://www.hanmanwang.com',
    description: '高质量韩国漫画在线阅读平台，提供免费最新韩国漫画资源',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@hanmanwang.com',
      availableLanguage: ['Chinese'],
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫网',
    description: '韩漫网品牌介绍、用户口碑和联系方式',
    url: 'https://www.hanmanwang.com/about',
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSubmitted(true);
      setFormError('');
      return;
    }

    setFormLoading(true);
    setFormError('');

    try {
      const formData = new FormData(form);
      const res = await fetch(contactFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const msg = (parsed as { meta?: { message?: string } })?.meta?.message
          || (parsed as { message?: string })?.message
          || responseText
          || '提交失败，请稍后重试';
        setFormError(msg);
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>

      <section className="bg-background-100/70">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-3">
            关于韩漫网
          </h1>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-16">
        <div className="flex flex-col lg:flex-row gap-12 items-center">
          <div className="flex-1">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
              品牌介绍
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">
              韩漫网成立于2024年，是国内领先的韩国漫画在线阅读平台。我们致力于为华语读者提供最新、最全、最高质量的韩国漫画资源。平台收录了数千部涵盖浪漫爱情、奇幻冒险、热血动作、都市剧情、悬疑惊悚等多种类型的优质韩漫作品。
            </p>
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">
              我们的使命是让每一位漫画爱好者都能方便、快捷地获取优质的韩国漫画内容，享受沉浸式的阅读体验。韩漫网团队由一群热爱漫画的年轻人组成，我们深知读者对内容质量和阅读体验的高要求。
            </p>
            <p className="text-sm text-foreground-600 leading-relaxed">
              我们相信漫画是一种跨越语言与文化界限的艺术形式。韩漫网将持续引入更多优质作品，打造华语世界最受欢迎的韩漫阅读社区。无论是热门连载还是经典完结作品，我们都力求以最快的速度、最好的质量呈现给读者。
            </p>
          </div>
          <div className="w-full lg:w-[420px] flex-shrink-0">
            <div className="rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20modern%20office%20space%20with%20bookshelves%20filled%20with%20comics%20and%20graphic%20novels%2C%20soft%20natural%20lighting%2C%20cozy%20reading%20nook%20with%20comfortable%20chairs%2C%20creative%20workspace%2C%20minimalist%20interior%20design%2C%20warm%20neutral%20tones&width=800&height=600&seq=about-brand-01&orientation=landscape"
                alt="韩漫网品牌形象"
                className="w-full aspect-[4/3] object-cover"
                width="800"
                height="600"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-16">
          {[
            { num: '5000+', label: '收录漫画', icon: 'ri-book-2-line' },
            { num: '200万+', label: '月活跃读者', icon: 'ri-user-heart-line' },
            { num: '99.8%', label: '服务可用率', icon: 'ri-shield-star-line' },
          ].map((stat, i) => (
            <div
              key={i}
              className="p-8 rounded-lg bg-background-100/70 text-center"
            >
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                <i className={`${stat.icon} text-xl`}></i>
              </div>
              <div className="text-3xl font-heading font-bold text-foreground-900 mb-2">
                {stat.num}
              </div>
              <p className="text-sm text-foreground-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100/70 py-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
            用户口碑
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, i) => (
              <div key={i} className="p-6 rounded-lg bg-background-50">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }, (_, j) => (
                    <i
                      key={j}
                      className={`text-sm ${
                        j < review.rating
                          ? 'ri-star-fill text-accent-500'
                          : 'ri-star-line text-foreground-300'
                      }`}
                    ></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "{review.content}"
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-label font-semibold text-foreground-800">
                    {review.name}
                  </span>
                  <span className="text-xs text-foreground-400">{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-16">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
          联系我们
        </h2>
        <div className="flex flex-col lg:flex-row gap-10">
          <div className="flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { icon: 'ri-mail-line', label: '联系邮箱', value: 'contact@hanmanwang.com' },
                { icon: 'ri-wechat-line', label: '官方微信', value: 'HanManWang_Official' },
                { icon: 'ri-qq-line', label: 'QQ群', value: '875629341' },
                { icon: 'ri-map-pin-line', label: '公司地址', value: '北京市朝阳区望京SOHO T3-A座 2508室' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-lg bg-background-100/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">{item.label}</p>
                    <p className="text-sm font-label font-semibold text-foreground-800">
                      {item.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="w-full lg:w-[400px] flex-shrink-0">
            <div className="p-6 rounded-lg bg-background-100/70">
              <h3 className="text-base font-heading font-bold text-foreground-900 mb-4">
                发送消息
              </h3>
              {formSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <p className="text-sm font-label font-semibold text-foreground-800">
                    消息已发送！
                  </p>
                  <p className="text-xs text-foreground-500 mt-1">
                    感谢您的来信，我们会尽快回复。
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form>
                  {formError && (
                    <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-600 text-xs">
                      {formError}
                    </div>
                  )}
                  <div className="space-y-4">
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    />
                    <textarea
                      name="message"
                      placeholder="请输入您的消息..."
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    ></textarea>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <button
                      type="submit"
                      disabled={formLoading}
                      className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-label font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {formLoading ? '发送中...' : '发送消息'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}